import api from './api';

const resource = 'Connect/';

export const getStatus = async (companyid) => {
  return await api.get(resource + 'Status?companyid=' + companyid);
};

export const getAccountDetails = async (companyid) => {
  return await api.get(resource + 'AccountDetails?companyid=' + companyid);
};

export const startOnboarding = async (companyid) => {
  return await api.post(resource + 'StartOnboarding?companyid=' + companyid);
};

export const refreshStatus = async (companyid) => {
  return await api.post(resource + 'Refresh?companyid=' + companyid);
};

export const resendLink = async (companyid) => {
  return await api.post(resource + 'ResendLink?companyid=' + companyid);
};

// Spec 2026-09-12 §5.1. The caller names a surface, never a component: which
// Stripe components the session carries — and what they may change — is the
// server's decision alone.
export const createAccountSession = async (companyid, surface) => {
  return await api.post(
    resource + 'AccountSession?companyid=' + companyid + '&surface=' + surface,
  );
};

export const getConnectBalance = async (companyid) => {
  return await api.get(resource + 'Balance?companyid=' + companyid);
};

// Finance Overview v3 (2026-10-11): "Your First Stripe Payout". The finance
// section reads it once per company (a database read, no Stripe) so the
// three pages that draw the banner agree, and closing it closes it on all.
export const getFirstPayoutNotice = async (companyid) => {
  return await api.get(resource + 'FirstPayoutNotice?companyid=' + companyid);
};

export const dismissFirstPayoutNotice = async (companyid) => {
  return await api.post(resource + 'FirstPayoutNotice/Dismiss?companyid=' + companyid);
};

// 'legacy' | 'new' while the banner should show; null otherwise — including
// any value this build does not know, which must never draw a banner with
// the wrong copy.
export const toFirstPayoutNotice = (res) => {
  const notice = res?.data?.notice;
  return notice === 'legacy' || notice === 'new' ? notice : null;
};

export const getReadiness = async (companyid) => {
  return await api.get(resource + 'Readiness?companyid=' + companyid);
};

export const activate = async (companyid) => {
  return await api.post(resource + 'Activate?companyid=' + companyid);
};

export const deactivate = async (companyid, reason) => {
  return await api.post(
    resource + 'Deactivate?companyid=' + companyid + '&reason=' + encodeURIComponent(reason),
  );
};

// Spec 2026-09-12 §4: fees.payer is fixed when a Stripe account is created.
// An account opened with Thisway as payer can never be activated; an admin
// replaces it with an account that pays its own Stripe fees and the
// restaurant onboards again. Answers with the bare status DTO, like
// StartOnboarding.
export const recreateAccount = async (companyid) => {
  return await api.post(resource + 'RecreateAccount?companyid=' + companyid);
};

export const getTerminals = async (companyid) => {
  return await api.get(resource + 'Terminals?companyid=' + companyid);
};

export const prepareTerminal = async (terminalid) => {
  return await api.post(resource + 'PrepareTerminal?terminalid=' + terminalid);
};

export const getActivationLog = async (companyid) => {
  return await api.get(resource + 'ActivationLog?companyid=' + companyid);
};

// ConnectController's period-scoped reads — the four Finance endpoints plus
// Disputes — filter `createdate >= from && createdate <= to` (both ends
// inclusive) on UTC timestamps, so callers pass ISO 8601 instants — the
// container builds them from local day boundaries. Either bound may be
// omitted; the API then leaves that side open.
const financeQuery = (companyid, from, to) => {
  let query = 'companyid=' + companyid;
  if (from) query += '&from=' + encodeURIComponent(from);
  if (to) query += '&to=' + encodeURIComponent(to);
  return query;
};

// The statement's own query: the period plus the store, plus the page when
// the caller is paging. `page` is only added with `pageSize`, so a half-set
// pair can never reach the server.
const orderReportQuery = (companyid, from, to, storeid, page, pageSize) => {
  let query = financeQuery(companyid, from, to);
  if (storeid) query += '&storeid=' + encodeURIComponent(storeid);
  if (page && pageSize) query += '&page=' + page + '&pageSize=' + pageSize;
  return query;
};

export const getFinanceSummary = async (companyid, from, to) => {
  return await api.get(resource + 'Finance/Summary?' + financeQuery(companyid, from, to));
};

export const getFinancePayments = async (companyid, from, to) => {
  return await api.get(resource + 'Finance/Payments?' + financeQuery(companyid, from, to));
};

export const getFinancePayouts = async (companyid, from, to) => {
  return await api.get(resource + 'Finance/Payouts?' + financeQuery(companyid, from, to));
};

export const getFinanceApplicationFees = async (companyid, from, to) => {
  return await api.get(
    resource + 'Finance/ApplicationFees?' + financeQuery(companyid, from, to),
  );
};

export const getRefunds = async (companyid) => {
  return await api.get(resource + 'Refunds?companyid=' + companyid);
};

export const getDisputes = async (companyid, from, to) => {
  return await api.get(resource + 'Disputes?' + financeQuery(companyid, from, to));
};

// The restaurant's order-by-order statement. Same financeQuery helper as the
// four Finance/* reads and Disputes, plus an optional storeid — "All Stores"
// omits it entirely rather than sending an empty value — and, since
// 2026-09-22, server-side paging: `page` is 1-based and `pageSize` is the
// grid's own 22. Both are omitted when the caller does not page (Overview's
// six-row preview asks for page 1 explicitly instead).
export const getOrderReports = async (companyid, from, to, storeid, page, pageSize) => {
  return await api.get(resource + 'Finance/OrderReports?' + orderReportQuery(companyid, from, to, storeid, page, pageSize));
};

// text/csv, not JSON: axios would otherwise try to parse a spreadsheet.
// The body IS the file — the caller hands it straight to downloadCsv.
export const exportOrderReports = async (companyid, from, to, storeid) => {
  const res = await api.get(
    resource + 'Finance/OrderReports/Export?' + orderReportQuery(companyid, from, to, storeid),
    { responseType: 'text', headers: { Accept: 'text/csv' } },
  );
  return res?.data ?? '';
};

// One payout's balance transactions, grouped by the API (spec 2026-09-22
// §8.3). The id is Stripe's own `po_…`, never our row GUID.
export const getPayoutDetail = async (companyid, payoutid) => {
  return await api.get(
    resource + 'Finance/PayoutDetail?companyid=' + companyid + '&payoutid=' + encodeURIComponent(payoutid),
  );
};

// The Upcoming row's own detail page (spec 2026-09-22 §13): the same DTO
// PayoutDetail sends — `toPayoutDetail` maps it unchanged — for the money
// not yet swept into any Stripe payout. No payoutid: there is no Stripe
// payout behind it yet.
// `arrival` (YYYY-MM-DD) asks for one Upcoming group — the row that was
// clicked — instead of every pending dollar (owner, 2026-09-23).
export const getUpcomingDetail = async (companyid, arrival) => {
  const date = arrival ? '&arrivaldate=' + encodeURIComponent(arrival) : '';
  return await api.get(resource + 'Finance/UpcomingDetail?companyid=' + companyid + date);
};

// Sets a restaurant's payout schedule (spec 2026-09-22 §8.2; open to the
// restaurant again since 2026-10-20). Callers: Overview's
// PayoutScheduleModal, the admin Connect tab's form, and the Manager app
// through its own client. The API admits an Admin with connect-manage, or
// an active company member with connect-manage who is not a paired device,
// and refuses the rest with 422. The body is ConnectPayoutScheduleRequest —
// { interval, weeklyanchor, monthlyanchor }, only the anchor the interval
// uses (payout-schedule-model.js builds it). Delay days are never sent:
// Stripe fixes them per country. Answers with the refreshed balance DTO, so
// the caller re-maps with toConnectBalance rather than issuing a second
// read — or with no body (204) when the save stood but that re-read failed.
export const updatePayoutSchedule = async (companyid, body) => {
  return await api.put(resource + 'PayoutSchedule?companyid=' + companyid, body);
};

// ConnectController returns the bare ConnectAccountStatusDto — it does not
// hand-wrap in Response<T> the way CompanySettingController and the rest of
// this API do, and there is no global result filter to do it for it. So the
// body IS the status: `res.data`, never `res.data.data`.
//
// The DTO is also all-lowercase (no camelCase serializer policy is configured),
// ships the status and payment model as ints beside their PascalCase names,
// and flattens Stripe's requirement sets into one comma-joined string. Rather
// than teach four screens three vocabularies, every reader goes through the
// adapter below and keeps speaking the shape the UI was designed around.

// EnumHelper.ConnectAccountStatusEnum on the API side. An int with no entry
// here resolves to undefined on purpose: the tab's UNKNOWN tone is the honest
// rendering of a status this build has no copy for.
const STATUS_BY_CODE = {
  0: 'NOT_STARTED',
  10: 'ONBOARDING',
  20: 'PENDING',
  30: 'RESTRICTED',
  40: 'READY',
};

// EnumHelper.PaymentModelEnum. The DTO defaults this to LegacyPlatform, so a
// company with no payment profile still arrives as 0 rather than absent.
const MODEL_BY_CODE = {
  0: 'LEGACY_PLATFORM',
  1: 'CONNECT_DIRECT',
  // Born for Connect, not live until admin Activate (spec 2026-09-25).
  2: 'CONNECT_PENDING',
};

// `requirementcodes` is CurrentlyDue + PastDue joined and de-duplicated
// (ConnectAccountStatusMapper.JoinRequirementCodes), so the three-way urgency
// split the UI can render is not recoverable from it. Stripe nests these sets
// — past_due is a subset of currently_due — so presenting the merged list as
// "Required now" is accurate; what is genuinely lost is eventually_due, which
// the DTO never carries at all.
//
// An empty result is null — "we have no checklist" — and never an empty
// checklist meaning "Stripe wants nothing". The two are not the same fact
// here, and reading null as the second is wrong: ConnectAccountStatusMapper
// returns Restricted when disabled_reason is set OR past_due is non-empty, so
// an account rejected, listed, under review or platform-paused reaches
// Restricted with BOTH code sets empty, and JoinRequirementCodes then returns
// null. Treating that as a confirmed-empty checklist told a restricted
// restaurant that Stripe had everything it needed.
//
// Returning null instead leaves both screens' `nothingOutstandingConfirmed`
// guards resting on the status axis — READY or PENDING — which is the only
// evidence that actually supports the claim. Under-claiming is the safe
// direction: "we don't have your checklist yet" is never a lie.
const parseRequirementCodes = (codes) => {
  const currentlyDue = String(codes || '')
    .split(',')
    .map((code) => code.trim())
    .filter(Boolean);
  if (!currentlyDue.length) return null;
  return { currentlyDue, pastDue: [], eventuallyDue: [] };
};

/**
 * Normalises a Connect response into the status shape the screens read.
 * Returns null when the body is not a status object, which every caller
 * already treats as "we could not read it" rather than as "no account".
 *
 * `onboardingUrl` and `clientSecret` are only ever populated by
 * StartOnboarding and ResendLink — GetStatus and Refresh pass null for both
 * (ConnectAccountService.ToDtoAsync), so neither can be minted on mount.
 */
export const toConnectStatus = (res) => {
  const dto = res?.data;
  if (!dto || typeof dto !== 'object') return null;

  // The DTO has no hasAccount field. The no-account branch of GetStatusAsync
  // builds its response without an id, and every branch that has a row
  // supplies one, so the id is an exact stand-in for the fact.
  const hasAccount = !!dto.stripeaccountid;

  return {
    hasAccount,
    stripeAccountId: dto.stripeaccountid || null,
    connectStatus: STATUS_BY_CODE[dto.connectstatus],
    chargesEnabled: !!dto.chargesenabled,
    payoutsEnabled: !!dto.payoutsenabled,
    detailsSubmitted: !!dto.detailssubmitted,
    feePayerVerified: !!dto.feepayerverified,
    paymentModel: MODEL_BY_CODE[dto.paymentmodel],
    requirements: parseRequirementCodes(dto.requirementcodes),
    // Stripe's own two sets, unmerged — `requirements` above is the
    // comma-joined legacy string and cannot answer "is anything PAST due".
    // The banner on Business Details asks exactly that (spec 2026-09-22 §9).
    currentlyDue: Array.isArray(dto.currentlydue) ? dto.currentlydue : [],
    pastDue: Array.isArray(dto.pastdue) ? dto.pastdue : [],
    currentDeadline: dto.currentdeadline || null,
    // Stripe's status on the bank or card it pays into, as Stripe states it:
    // `errored` once a payout has bounced off it, which stops scheduled
    // payouts until the restaurant changes it. Null when the API cannot tell
    // or does not send the field yet — "nothing to say", never "all good".
    payoutDestinationStatus: dto.payoutdestinationstatus || null,
    onboardingUrl: dto.onboardingurl || '',
    clientSecret: dto.accountsessionclientsecret || '',
  };
};

/**
 * The Account page's record. Every field is optional by contract, not by
 * accident: Stripe returns the business-profile half of a connected account
 * reliably and withholds most of the KYC half (measured across ten submitted
 * accounts on 2026-09-16 — see the API's ConnectAccountDetailsDto). `null`
 * here means "do not draw this row", so the adapter normalises absent, empty
 * and whitespace-only alike rather than letting `''` through to be rendered
 * as a blank value.
 */
export const toAccountDetails = (res) => {
  const dto = res?.data;
  if (!dto || typeof dto !== 'object') return null;
  const text = (value) =>
    typeof value === 'string' && value.trim() ? value.trim() : null;

  return {
    hasRecord: !!dto.hasrecord,
    legalName: text(dto.legalname),
    publicName: text(dto.publicname),
    businessType: text(dto.businesstype),
    businessStructure: text(dto.businessstructure),
    country: text(dto.country),
    website: text(dto.website),
    supportPhone: text(dto.supportphone),
    supportAddress: text(dto.supportaddress),
    legalAddress: text(dto.legaladdress),
    // The MCC as a label, resolved server-side; an MCC we have no words for
    // arrives null and the row is not drawn.
    industry: text(dto.industry),
    // Three answers, not two: true/false is Stripe's word, null is "Stripe
    // said nothing" and the row is omitted. Never the number itself.
    taxIdProvided: typeof dto.taxidprovided === 'boolean' ? dto.taxidprovided : null,
    statementDescriptor: text(dto.statementdescriptor),
    representativeName: text(dto.representativename),
    representativeEmail: text(dto.representativeemail),
    representativeRole: text(dto.representativerole),
    // Three answers, not two: true/false is Stripe's word on the person,
    // null is "Stripe said nothing" and the page omits the row.
    representativeVerified:
      typeof dto.representativeverified === 'boolean' ? dto.representativeverified : null,
    destinationName: text(dto.destinationname),
    destinationLast4: text(dto.destinationlast4),
    destinationCurrency: text(dto.destinationcurrency),
  };
};

const ACTION_BY_CODE = { 0: 'START_ONBOARDING', 10: 'ACTIVATE', 20: 'DEACTIVATE', 30: 'ACCOUNT_RECREATED' };

/**
 * One shape from two. GET /Connect/Readiness reports `ready` and `blockers` at
 * the top level and repeats them inside `snapshot`; Activate and Deactivate
 * return neither at the top level and carry `readiness` instead. Reading the
 * snapshot first and falling back to the top level covers both without any
 * caller knowing which endpoint answered.
 */
export const toReadiness = (res) => {
  const body = res?.data;
  if (!body || typeof body !== 'object') return null;
  const snap = body.readiness || body.snapshot;
  if (!snap || typeof snap !== 'object') return null;

  return {
    ready: snap.ready ?? body.ready ?? false,
    blockers: Array.isArray(snap.blockers)
      ? snap.blockers
      : Array.isArray(body.blockers)
        ? body.blockers
        : [],
    paymentModel: MODEL_BY_CODE[body.paymentmodel],
    // Where Deactivate would put the company (owner, 2026-09-25): back to
    // Pending for a company born Pending, legacy otherwise. Undefined from an
    // API that predates the field, which reads as legacy, as it always did.
    deactivateModel: MODEL_BY_CODE[body.deactivatemodel],
    connectStatus: STATUS_BY_CODE[snap.connectstatus],
    feePayerVerified: !!snap.feepayerverified,
    chargesEnabled: !!snap.chargesenabled,
    payoutsEnabled: !!snap.payoutsenabled,
    terminalCount: snap.terminalcount ?? 0,
    preparedTerminalCount: snap.preparedterminalcount ?? 0,
    webhookSecretConfigured: !!snap.webhooksecretconfigured,
    hasAccountUpdatedWebhook: !!snap.hasaccountupdatedwebhook,
  };
};

export const toTerminals = (res) => {
  const rows = res?.data;
  if (!Array.isArray(rows)) return [];
  return rows.map((r) => ({
    terminalId: r.terminalid,
    name: r.name || '',
    code: r.code || null,
    storeId: r.storeid,
    storeName: r.storename || null,
    readerConnected: !!r.readerconnected,
    prepared: !!r.prepared,
    stripeLocationId: r.stripelocationid || null,
  }));
};

export const toActivationLog = (res) => {
  const rows = res?.data;
  if (!Array.isArray(rows)) return [];
  return rows.map((r) => ({
    id: r.id,
    action: ACTION_BY_CODE[r.action],
    fromModel: MODEL_BY_CODE[r.frommodel],
    toModel: MODEL_BY_CODE[r.tomodel],
    actorAdminId: r.actoradminid || null,
    reason: r.reason || null,
    createdAt: r.createdate || null,
  }));
};

// Every Connect finance DTO carries integer cents (C# long). Screens format
// dollars with formatCurrency, so the conversion happens here, once.
const dollars = (cents) => (typeof cents === 'number' ? cents / 100 : 0);

// Buy-rate fields are null for non-admins (ConnectFinanceService,
// includeBuyRate). They are added to the output only when the API actually
// sent a number, so a restaurant-scoped object has no such key at all — a
// card grid that reads "keys present" can then never leak a margin.
const withBuyRate = (target, pairs) => {
  pairs.forEach(([key, cents]) => {
    if (typeof cents === 'number') target[key] = dollars(cents);
  });
  return target;
};

const listOf = (res) => (Array.isArray(res?.data) ? res.data : []);

/**
 * Normalises Connect/Finance/Summary into dollars. Returns null when the
 * body is not a summary object (the 422 body is a bare string).
 *
 * `applicationFees` is present for every viewer because the API sends it to
 * everyone (backend TODO C1); the restaurant viewer must simply never render
 * it. `stripeProcessingFee` / `cardFeeMargin` exist only when
 * `includesbuyrate` is true and the API filled them.
 *
 * `feesNotCollected` / `otherDeductions` close the Gross − fees = Net
 * identity on the rows a capped application fee (or some other deduction
 * outside the three listed fees) would otherwise break. Both are
 * restaurant-safe — the same commercial figures every viewer already sees —
 * so, unlike the buy-rate pair above, they are always mapped.
 */
export const toFinanceSummary = (res) => {
  const dto = res?.data;
  if (!dto || typeof dto !== 'object') return null;
  const summary = {
    companyId: dto.companyid || null,
    paymentCount: dto.paymentcount || 0,
    gross: dollars(dto.grossamount),
    // The customer-paid platform fee passes through to Thisway (spec §3).
    platformFees: dollars(dto.platformfees),
    thiswayFee: dollars(dto.thiswayfee),
    // What the restaurant actually paid for processing: its agreed fee, or
    // Stripe's higher estimate on an international card. The agreed rate
    // alone would make Gross − fees ≠ Net on those rows.
    cardFee: dollars(dto.cardfeecollected),
    restaurantNet: dollars(dto.restaurantnet),
    applicationFees: dollars(dto.applicationfees),
    payoutCount: dto.payoutcount || 0,
    payoutAmount: dollars(dto.payoutamount),
    internationalCount: dto.internationalcount || 0,
    includesBuyRate: dto.includesbuyrate === true,
    feesNotCollected: dollars(dto.feesnotcollected),
    otherDeductions: dollars(dto.otherdeductions),
    // Succeeded refunds against this period's payments. The API has already
    // taken these out of restaurantnet; this is here so the card can show the
    // subtraction rather than leave Gross - fees = Net looking wrong.
    refundAmount: dollars(dto.refundamount),
    refundCount: dto.refundcount || 0,
  };
  if (!summary.includesBuyRate) return summary;
  return withBuyRate(summary, [
    ['stripeProcessingFee', dto.stripeprocessingfee],
    ['cardFeeMargin', dto.cardfeemargin],
  ]);
};

export const toFinancePayments = (res) =>
  listOf(res).map((row) =>
    withBuyRate(
      {
        id: row.id,
        orderId: row.orderid || null,
        paymentIntentId: row.paymentintentid || null,
        gross: dollars(row.grossamount),
        platformFee: dollars(row.platformfee),
        thiswayFee: dollars(row.thiswayfee),
        cardFee: dollars(row.cardfeecollected),
        restaurantNet: dollars(row.restaurantnet),
        applicationFee: dollars(row.applicationfeeamount),
        international: row.international === true,
        status: row.status || '',
        createdAt: row.createdate,
      },
      [
        ['stripeProcessingFee', row.actualstripeprocessingfee],
        ['cardFeeMargin', row.cardfeemargin],
        ['reconciliationDelta', row.feereconciliationdelta],
      ],
    ),
  );

export const toFinancePayouts = (res) =>
  listOf(res).map((row) => ({
    id: row.id,
    stripePayoutId: row.stripepayoutid || null,
    amount: dollars(row.amount),
    currency: row.currency || 'usd',
    status: row.status || '',
    arrivalDate: row.arrivaldate || null,
    destination: row.destination || null,
    createdAt: row.createdate,
    // Stripe's `method` and the destination bank, resolved by the API's
    // payout ingest (spec 2026-09-21 §6.1). Null on rows from before the
    // columns existed; the tables draw "—" and no Instant tag for those.
    method: row.method || null,
    destinationBankName: row.destinationbankname || null,
    destinationLast4: row.destinationlast4 || null,
    // A failed payout the bank sent back after depositing it (owner's
    // "Payout States" mock, frame 5f). Stripe calls both `failed`; the API
    // tells them apart by the paid stamp the row kept. An older API sends
    // no flag, which reads as a plain failure.
    returned: row.returned === true,
    failureCode: row.failurecode || null,
  }));

export const toFinanceApplicationFees = (res) =>
  listOf(res).map((row) => ({
    id: row.id,
    connectPaymentId: row.connectpaymentid || null,
    stripeApplicationFeeId: row.stripeapplicationfeeid || null,
    amount: dollars(row.amount),
    status: row.status || '',
    createdAt: row.createdate,
  }));

export const toRefunds = (res) =>
  listOf(res).map((row) => ({
    id: row.id,
    orderId: row.orderid || null,
    stripeRefundId: row.striperefundid || null,
    paymentIntentId: row.paymentintentid || null,
    chargeId: row.chargeid || null,
    amount: dollars(row.amount),
    status: row.status || '',
    reason: row.reason || null,
    createdAt: row.createdate,
  }));

export const toDisputes = (res) =>
  listOf(res).map((row) => ({
    id: row.id,
    orderId: row.orderid || null,
    stripeDisputeId: row.stripedisputeid || null,
    chargeId: row.chargeid || null,
    amount: dollars(row.amount),
    status: row.status || '',
    reason: row.reason || null,
    createdAt: row.createdate,
  }));

// NOTE: dollars, not cents. Every field here comes from the Order row, which
// stores decimals — unlike every other adapter in this file. Do not reach for
// `dollars()` on these.
//
// `orderStatus` is a raw int (Order.orderstatus, not a string) — there is no
// client-facing label from the server at all here, unlike every `status`
// field above. The label comes from the existing `OrderStatus` map in
// src/data/enums.js, the same one company-detail/order-list.js already uses.
const toOrderReportRow = (row) => ({
  orderId: row.orderid || null,
  orderNumber: row.ordernumber || '',
  createdAt: row.createdate || null,
  // When the money went back, which is a different question from when the
  // order was placed. Null on an order that was never refunded.
  refundDate: row.refunddate || null,
  orderStatus: row.orderstatus ?? null,
  storeId: row.storeid || null,
  storeName: row.storename || '',
  // Where the order was placed (EnumHelper.PlatformEnum) and its store's
  // timezone, so the finance tables name the platform and read Created in
  // the store's own time (owner, 2026-09-25).
  platform: row.platform ?? null,
  timezoneId: row.timezoneid || null,
  subtotal: row.subtotal || 0,
  tax: row.tax || 0,
  crvTax: row.crvtax || 0,
  storeFees: row.storefees || 0,
  tip: row.tip || 0,
  customerFee: row.customerfee || 0,
  // Paid by the customer and taken back out as its own deduction (owner,
  // 2026-09-25): Order Reports draws it as Platform Fee (paid) and Platform
  // Fee.
  platformFee: row.platformfee || 0,
  cardFees: row.cardfees || 0,
  discount: row.discount || 0,
  thiswayFee: row.thiswayfee || 0,
  refund: row.refund || 0,
  netPayout: row.netpayout || 0,
  // Null means "not known yet". Never coerced to a date.
  fundsAvailableOn: row.fundsavailableon || null,
  // 'full' | 'partial' | null — the server judges it against what the
  // customer paid. Neither Status column reads it any more, grid or export
  // (owner, 2026-09-25: Status is the money's Available / Pending; the
  // Refund column carries the refund).
  refundStatus: row.refundstatus || null,
});

// Returns null for a body that is not a report (the 422 body is a bare
// string) — the caller's signal to show an error rather than paint zeros
// over a refused read.
export const toOrderReports = (res) => {
  const dto = res?.data;
  if (!dto || typeof dto !== 'object') return null;
  const totals = dto.totals || {};
  const rows = Array.isArray(dto.rows) ? dto.rows.map(toOrderReportRow) : [];
  return {
    rows,
    // The whole filter's count, not this page's: the footer says "of 96
    // orders" while showing 22. An older API with no paging sends no count,
    // and the page length is then the honest answer.
    totalCount: typeof dto.totalcount === 'number' ? dto.totalcount : rows.length,
    // Whole-filter column visibility (API `hascrv` / `hascustomerfee`): the
    // page's 22 rows cannot know whether a CRV or Customer Fee row exists on
    // page 3, and the CSV export decides over the whole filter — the grid must
    // agree with the file. Absent on an older API → null → the page scans its
    // own rows instead.
    optional:
      typeof dto.hascrv === 'boolean' || typeof dto.hascustomerfee === 'boolean'
        ? {
            crvTax: dto.hascrv === true,
            customerFee: dto.hascustomerfee === true,
            platformFee: dto.hasplatformfee === true,
          }
        : null,
    // Also dollars, not cents — same Order-backed source as toOrderReportRow
    // above, outside that comment's own line range but under the same rule.
    totals: {
      orderCount: totals.ordercount || 0,
      tax: totals.tax || 0,
      tip: totals.tip || 0,
      discount: totals.discount || 0,
      refund: totals.refund || 0,
      netPayout: totals.netpayout || 0,
    },
  };
};

// One payout's transactions, as the detail page reads them (spec 2026-09-22
// §6/§8.3). Cents in, dollars out — the same rule every other Connect DTO
// follows, and the one thing the order-report rows above are exempt from.
//
// Rows keep their nulls: a charge the API could not match to an order still
// appears, with `orderNumber` null (drawn as "—"), because dropping it would
// stop the groups adding up to the payout.
const toPayoutRow = (row) => ({
  type: row.type || '',
  // Stripe's own balance-transaction type (API 2026-10-07), next to the tab:
  // a `payout_failure` — a failed payout's money coming back — is named
  // "Payout returned", not "Adjustment". An older API sends none.
  kind: row.kind || null,
  // Stripe's reporting category (API 2026-10-07): the word the Transactions
  // list and the CSV name the row by, the same words the Summary's lines
  // use. An older API sends none.
  category: row.category || null,
  stripeId: row.stripeid || null,
  orderId: row.orderid || null,
  orderNumber: row.ordernumber || null,
  storeId: row.storeid || null,
  storeName: row.storename || null,
  platform: row.platform ?? null,
  timezoneId: row.timezoneid || null,
  date: row.date || null,
  amount: dollars(row.amount),
});

const toPayoutGroup = (group) => ({
  count: group?.count || 0,
  net: dollars(group?.net),
});

// One of the Summary's lines (API 2026-10-07): Stripe's own classification,
// only the lines that carry something, in the card's order. The key is
// what the card knows words for; the label is the API's own, for a key it
// does not.
const toSummaryLine = (line) => ({
  key: line?.key || '',
  label: line?.label || '',
  count: line?.count || 0,
  net: dollars(line?.net),
});

const rowsOf = (value) => (Array.isArray(value) ? value.map(toPayoutRow) : []);

// The admin Finance page's Connect accounts list (owner, 2026-09-24): every
// company with a connected account, its three status facts and its routing,
// off the API's own webhook-kept columns.
export const getConnectAccounts = async () => {
  return await api.get(resource + 'Accounts');
};

export const toConnectAccounts = (res) => {
  const rows = Array.isArray(res?.data) ? res.data : [];
  return rows.map((row) => ({
    companyId: row.companyid || '',
    companyName: row.companyname || '',
    logo: row.companylogo || null,
    stripeAccountId: row.stripeaccountid || null,
    // An older API sends no flag; it listed only accounts, so having an id
    // meant having an account.
    hasAccount: row.hasaccount === undefined ? !!row.stripeaccountid : row.hasaccount === true,
    statusName: row.connectstatusname || '',
    paymentModel: MODEL_BY_CODE[row.paymentmodel] || 'LEGACY_PLATFORM',
    chargesEnabled: row.chargesenabled === true,
    payoutsEnabled: row.payoutsenabled === true,
    outstanding: typeof row.outstanding === 'number' ? row.outstanding : 0,
    updatedAt: row.updatedate || null,
  }));
};

export const toPayoutDetail = (res) => {
  const dto = res?.data;
  if (!dto || typeof dto !== 'object') return null;
  const summary = dto.summary || {};
  return {
    // Flat, exactly as ConnectPayoutDetailDto sends it: the payout's own
    // facts sit beside its groups rather than inside a `details` envelope,
    // which is a shape the API never had.
    stripePayoutId: dto.stripepayoutid || null,
    // The payout's own amount — the figure the bank will show, and the one
    // the page header prints. `summary.total` carries the same number on the
    // wire, but this is the field the header is about.
    amount: dollars(dto.amount),
    currency: dto.currency || 'usd',
    status: dto.status || '',
    method: dto.method || null,
    createdAt: dto.createdate || null,
    arrivalDate: dto.arrivaldate || null,
    // The payouts list already calls these `destinationbankname` /
    // `destinationlast4`; the detail read uses the same two names.
    bankName: dto.destinationbankname || null,
    bankLast4: dto.destinationlast4 || null,
    traceId: dto.traceid || null,
    descriptor: dto.descriptor || null,
    // Frames 5d to 5f of the owner's "Payout States" mock: why a payout
    // failed (Stripe's failure_code / failure_message), when its status
    // last changed (the day it failed, was canceled or was returned), when
    // it was deposited, and whether a failed payout had been paid first —
    // the bank sent it back. Null and false on an older API.
    failureCode: dto.failurecode || null,
    failureMessage: dto.failuremessage || null,
    paidAt: dto.paidat || null,
    statusAt: dto.statusat || null,
    returned: dto.returned === true,
    scheduleDelayDays:
      typeof dto.scheduledelaydays === 'number' ? dto.scheduledelaydays : null,
    // Whether the transactions add up to the payout. Deliberately unused by
    // the UI — the page prints the payout's own amount either way, and the
    // API logs the mismatch — but mapped, so the seam test can see it and so
    // it is not a field we would have to re-plumb to show it later.
    reconciled: dto.reconciled === true,
    // Whether Stripe itemized the payout into balance transactions. False
    // for a payout sent by hand — standard on request, or instant — which
    // Stripe will not break down: the API then sends the payout's own facts
    // and empty groups, and the page says why (owner, 2026-09-23). An older
    // API sends no flag, and every payout it could answer for was itemized,
    // so only an explicit false counts.
    itemized: dto.itemized !== false,
    summary: {
      charges: toPayoutGroup(summary.charges),
      refunds: toPayoutGroup(summary.refunds),
      adjustments: toPayoutGroup(summary.adjustments),
      // Upcoming only (owner, 2026-09-24): the manual payouts that already
      // took some of this money, and whatever the rows leave unexplained,
      // so the summary adds up to the balance's own figure. A real payout's
      // page — and an older API — sends neither, which reads as nothing.
      paidOut: toPayoutGroup(summary.paidout),
      other: dollars(summary.other),
      // The lines by Stripe's classification (API 2026-10-07). Null, not
      // [], from an older API: the card then draws the three groups above.
      lines: Array.isArray(summary.lines) ? summary.lines.map(toSummaryLine) : null,
      // The payout's own amount wins over the sum of the groups: the API
      // logs a mismatch and sends the payout amount, because that is the
      // figure the bank will show (spec §8.3).
      total: dollars(summary.total),
    },
    charges: rowsOf(dto.charges),
    refunds: rowsOf(dto.refunds),
    adjustments: rowsOf(dto.adjustments),
    // Upcoming only (owner, 2026-10-07): the manual payouts that went out,
    // as rows, so the Transactions list adds up to what the summary's
    // "Payouts" line says. A real payout's page — and an older
    // API — sends none.
    payouts: rowsOf(dto.payouts),
  };
};

/**
 * The single-use secret the browser hands Connect.js. Null when the body is
 * not a session — a refusal arrives as a bare string, and mounting Stripe
 * against `undefined` shows an empty frame with no explanation.
 */
export const toAccountSession = (res) => {
  const dto = res?.data;
  if (!dto || typeof dto !== 'object' || typeof dto.clientsecret !== 'string') return null;
  return { clientSecret: dto.clientsecret, expiresAt: dto.expiresat ?? null };
};

/**
 * What is in the account right now. `nextPayout*` are null whenever Stripe has
 * no payout on the way — the strip then names the schedule instead, because a
 * date we invented from the schedule is not a date Stripe promised.
 *
 * `bank*` are new fields (fix wave B): the admin support block reads them
 * instead of mounting the account-details component, now that
 * `account_management` is gone from the `admin` Account Session surface —
 * setting every write flag false there did not make that component
 * read-only, it made it the wrong component to hand an admin at all. Stripe
 * sends all three together, and all three null together whenever the account
 * has no external bank account on file.
 */
export const toConnectBalance = (res) => {
  const dto = res?.data;
  if (!dto || typeof dto !== 'object') return null;
  // Finance Overview v3 (designer, 2026-10-11). Stripe's `pending` carries
  // the debit of a payout still on its way and goes below zero the day after
  // a payout run (Van Ness, 2026-10-12: −$307.64), so the card shows neither
  // it nor `available`. The API (thisway-api #182) counts in `intransit` every
  // payout Stripe has created and not yet paid, sent or not (Stripe's own "On
  // the way to your bank"), and `processing` is available + pending only: what
  // the next payouts will carry. The total is added here in cents. An older API
  // without the two reads as nothing on its way and available + pending, which
  // is what `processing` now means — today's figures, never a blank card.
  const centsOf = (value) => (typeof value === 'number' ? value : 0);
  const inTransitCents = centsOf(dto.intransit);
  const processingCents =
    typeof dto.processing === 'number' ? dto.processing : centsOf(dto.available) + centsOf(dto.pending);
  return {
    available: dollars(dto.available),
    pending: dollars(dto.pending),
    inTransit: inTransitCents / 100,
    processing: processingCents / 100,
    totalBalance: (inTransitCents + processingCents) / 100,
    currency: dto.currency || 'usd',
    nextPayoutDate: dto.nextpayoutdate || null,
    nextPayoutAmount:
      typeof dto.nextpayoutamount === 'number' ? dollars(dto.nextpayoutamount) : null,
    // Stripe's own flag on the payout object — a payout requested by hand
    // under a daily schedule is still manual. Null without a next payout.
    nextPayoutAutomatic:
      typeof dto.nextpayoutautomatic === 'boolean' ? dto.nextpayoutautomatic : null,
    scheduleInterval: dto.scheduleinterval || null,
    scheduleDelayDays:
      typeof dto.scheduledelaydays === 'number' ? dto.scheduledelaydays : null,
    // Which day of the week / month the schedule fires on. Null unless the
    // interval is weekly / monthly — the settings modal shows the second
    // dropdown only then (spec 2026-09-22 §5.2).
    scheduleWeeklyAnchor: dto.scheduleweeklyanchor || null,
    scheduleMonthlyAnchor:
      typeof dto.schedulemonthlyanchor === 'number' ? dto.schedulemonthlyanchor : null,
    bankName: dto.bankname || null,
    bankLast4: dto.banklast4 || null,
    bankCurrency: dto.bankcurrency || null,
    // What could leave the account now rather than on the schedule. Stripe
    // decides instant-payout eligibility itself (it carries the losses here),
    // so this is its answer, not ours to compute. `instantNetAvailable` is
    // what the restaurant would actually receive; null means no destination
    // Stripe will pay instantly, which is a "no" whatever the gross says.
    instantAvailable: dollars(dto.instantavailable),
    instantNetAvailable:
      typeof dto.instantnetavailable === 'number' ? dollars(dto.instantnetavailable) : null,
    // Round 2 (spec 2026-09-22 §4/§13): the money already settled into the
    // account, grouped by the day Stripe expects to sweep it into a payout,
    // ascending as the API sends it. The hero's Overview rail reads group 0
    // for its own "Next payout" row; the Payouts/Overview activity tables
    // read the whole array for one Upcoming row per arrival date.
    upcomingGroups: Array.isArray(dto.upcominggroups)
      ? dto.upcominggroups.map((g) => ({
        arrivalDate: g.arrivaldate || null,
        amount: dollars(g.amount),
        count: typeof g.count === 'number' ? g.count : 0,
      }))
      : [],
  };
};

const connectApi = {
  getStatus,
  getAccountDetails,
  startOnboarding,
  refreshStatus,
  resendLink,
  createAccountSession,
  getConnectBalance,
  getReadiness,
  getFirstPayoutNotice,
  dismissFirstPayoutNotice,
  activate,
  deactivate,
  recreateAccount,
  getTerminals,
  prepareTerminal,
  getActivationLog,
  getFinanceSummary,
  getFinancePayments,
  getFinancePayouts,
  getFinanceApplicationFees,
  getRefunds,
  getDisputes,
  getOrderReports,
  exportOrderReports,
  getPayoutDetail,
  getUpcomingDetail,
  updatePayoutSchedule,
  toConnectStatus,
  toAccountDetails,
  toAccountSession,
  toConnectBalance,
  toReadiness,
  toFirstPayoutNotice,
  toTerminals,
  toActivationLog,
  toFinanceSummary,
  toFinancePayments,
  toFinancePayouts,
  toFinanceApplicationFees,
  toRefunds,
  toDisputes,
  toOrderReports,
  toPayoutDetail,
  getConnectAccounts,
  toConnectAccounts,
};

export default connectApi;
